import { Component, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import {
  RepairTaskFilterDto,
  RepairTaskFilterOptionDto,
  RepairTaskFilterOptionsDto,
  RepairTaskItemDto,
  RepairTaskService,
} from '@artemis-software/wr-api';
import { DataTableComponent } from '@shared/generic/data-table/data-table.component';
import { GenericDialogComponent } from '@components/dialog/generic-dialog/generic-dialog.component';
import { RepairTaskDispatchService } from '@services/repair-task-dispatch.service';
import { RepairTaskStatusChangeService } from '@services/repair-task-status-change.service';
import { openRepairTaskEditDialog } from '@components/dialog/repair-task-edit-dialog/repair-task-edit-dialog.component';

const REPAIR_TASK_SOURCE_LABELS: Record<RepairTaskFilterDto.SourceEnum, string> = {
  INSPECTION_ENTRY: 'Prüfpunktzeile',
  BUILDING_EQUIPMENT: 'TGA',
};

@Component({
  standalone: false,
  selector: 'wr-building-repair-task-overview',
  templateUrl: './building-repair-task-overview.component.html',
  styleUrl: './building-repair-task-overview.component.scss',
})
export class BuildingRepairTaskOverviewComponent {
  private readonly repairTaskService = inject(RepairTaskService);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);
  private readonly fb = inject(FormBuilder);
  private readonly dispatchService = inject(RepairTaskDispatchService);
  private readonly statusChangeService = inject(RepairTaskStatusChangeService);

  readonly buildingId = input.required<string | null>();
  readonly buildingEquipmentId = input<string | null>(null);
  readonly localStorageIdentifier = computed(() =>
    this.buildingEquipmentId() ? 'building-equipment-repair-task-overview' : 'building-repair-task-overview');
  private readonly dataTable = viewChild(DataTableComponent);

  readonly statuses = Object.values(RepairTaskItemDto.StatusEnum);
  readonly sources = Object.values(RepairTaskFilterDto.SourceEnum);
  readonly sourceLabels = REPAIR_TASK_SOURCE_LABELS;

  readonly form = this.fb.group({
    text: [''],
    statuses: [[] as RepairTaskItemDto.StatusEnum[]],
    contractorId: [null as string | null],
    buildingEquipmentId: [null as string | null],
    source: [null as RepairTaskFilterDto.SourceEnum | null],
  });

  readonly contractorOptions = signal<RepairTaskFilterOptionDto[]>([]);
  readonly buildingEquipmentOptions = signal<RepairTaskFilterOptionDto[]>([]);

  private lineClickFromActions = false;

  constructor() {
    effect(() => this.loadFilterOptions(this.buildingId()));
  }

  readonly loadItems = (filter: RepairTaskFilterDto) =>
    this.repairTaskService.findAll(this.toFilter(filter));

  readonly getCount = (filter: RepairTaskFilterDto) =>
    this.repairTaskService.getCount(this.toFilter(filter));

  origin(task: RepairTaskItemDto): string {
    if (task.buildingEquipment) {
      return `TGA: ${task.buildingEquipment.equipmentName}`;
    }
    if (task.inspectionEntryCheckpoint) {
      return `Prüfpunktzeile: ${task.inspectionEntryCheckpoint.name}`;
    }
    return '–';
  }

  readonly exportOrigin = (task: RepairTaskItemDto): string => this.origin(task);

  ignoreLineClick(): void {
    this.lineClickFromActions = true;
    setTimeout(() => this.lineClickFromActions = false);
  }

  async open(task: RepairTaskItemDto): Promise<void> {
    if (this.lineClickFromActions) {
      return;
    }
    await openRepairTaskEditDialog(this.dialogService, task.id);
    this.refresh();
  }

  async changeStatus(task: RepairTaskItemDto, status: RepairTaskItemDto.StatusEnum): Promise<void> {
    if (status === task.status) {
      return;
    }
    await this.statusChangeService.changeStatus([task], status);
    this.refreshTable();
  }

  async resend(task: RepairTaskItemDto): Promise<void> {
    const sent = await this.dispatchService.sendMail(task.id);
    if (sent) {
      this.refresh();
    }
  }

  async delete(task: RepairTaskItemDto): Promise<void> {
    const confirmed = await firstValueFrom(this.dialogService.open(GenericDialogComponent, {
      context: {
        header: 'Reparaturauftrag löschen',
        message: `Soll der Reparaturauftrag „${task.title}“ gelöscht werden?`,
        status: 'danger',
      },
    }).onClose);
    if (!confirmed) {
      return;
    }
    this.repairTaskService.deleteById(task.id).subscribe(() => {
      this.toastrService.success('Reparaturauftrag gelöscht', 'Reparaturauftrag');
      this.refresh();
    });
  }

  refresh(): void {
    this.refreshTable();
    this.loadFilterOptions(this.buildingId());
  }

  private refreshTable(): void {
    this.dataTable()?.refresh$.next();
  }

  private loadFilterOptions(buildingId: string | null): void {
    if (!buildingId || buildingId === 'new') {
      return;
    }
    this.repairTaskService.findFilterOptionsByBuildingId(buildingId).subscribe(options => {
      this.contractorOptions.set(options.contractors);
      this.buildingEquipmentOptions.set(options.buildingEquipments);
      this.dropSelectionsOutside(options);
    });
  }

  private dropSelectionsOutside(options: RepairTaskFilterOptionsDto): void {
    const { contractorId, buildingEquipmentId } = this.form.getRawValue();
    const staleContractor = !!contractorId && !options.contractors.some(option => option.id === contractorId);
    const staleEquipment = !!buildingEquipmentId &&
      !options.buildingEquipments.some(option => option.id === buildingEquipmentId);
    if (!staleContractor && !staleEquipment) {
      return;
    }
    this.form.patchValue({
      contractorId: staleContractor ? null : contractorId,
      buildingEquipmentId: staleEquipment ? null : buildingEquipmentId,
    });
    this.dataTable()?.applyFormValueToFilter();
  }

  private toFilter(filter: RepairTaskFilterDto): RepairTaskFilterDto {
    return {
      ...filter,
      buildingId: this.buildingId() ?? undefined,
      statuses: filter.statuses?.length ? filter.statuses : undefined,
      contractorId: filter.contractorId ?? undefined,
      buildingEquipmentId: this.buildingEquipmentId() ?? filter.buildingEquipmentId ?? undefined,
      source: this.buildingEquipmentId() ? undefined : filter.source ?? undefined,
    };
  }
}
